import React from "react";
import { MapPin, ShieldCheck, Calendar } from "lucide-react";
import { motion } from "framer-motion";
import CarNavigating from "../../assets/CarNavigating.jpg";

export default function Customers({ handleScroll }) {
  const features = [
    {
      icon: <MapPin className="w-5 h-5 text-[#007AFF]" />,
      title: "Smart Location Services",
      description:
        "Find the nearest service providers with real-time availability and transparent pricing.",
      offsetClass: "",
    },
    {
      icon: <ShieldCheck className="w-5 h-5 text-[#007AFF]" />,
      title: "Complete Peace of Mind",
      description:
        "Access emergency assistance and a reliable network for worry-free, long-term driving.",
      offsetClass: "md:pt-10",
    },
    {
      icon: <Calendar className="w-5 h-5 text-[#007AFF]" />,
      title: "Effortless Scheduling",
      description:
        "Book maintenance appointments that perfectly fit your schedule with trusted professionals.",
      offsetClass: "",
    },
  ];

  return (
    <section id="customers" className="relative py-20 sm:py-28 bg-[#F9FAFB] border-t border-slate-100 overflow-hidden">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        
        {/* Header Content */}
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.2 }}
          transition={{ duration: 0.5 }}
          className="text-center max-w-2xl mx-auto mb-16"
        >
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-extrabold tracking-tight text-[#007AFF] mb-4">
            Drive with Unwavering Confidence
          </h2>
          <p className="text-sm md:text-base text-slate-500 font-medium leading-relaxed">
            Americar is your trusted co-pilot, ensuring your vehicle is always safe, healthy, and ready for the road.
          </p>
        </motion.div>

        {/* 3 Features with Separate Dedicated Connecting Lines */}
        <div className="relative mb-16 max-w-5xl mx-auto">
          
          {/* Connector 1 (Desktop only): Direct bridge from Circle 1 to Circle 2 with Animated Flow */}
          <div className="hidden md:block absolute top-4 left-[20%] w-[28%] h-14 pointer-events-none z-0">
            <svg className="w-full h-full" viewBox="0 0 100 40" fill="none" preserveAspectRatio="none">
              <path 
                d="M 0 5 C 45 5, 55 35, 100 35" 
                stroke="#93C5FD" 
                strokeWidth="2" 
                strokeDasharray="4 4" 
                fill="none" 
                className="animate-dash-flow"
              />
            </svg>
          </div>

          {/* Connector 2 (Desktop only): Direct bridge from Circle 2 to Circle 3 with Animated Flow */}
          <div className="hidden md:block absolute top-4 left-[52%] w-[28%] h-14 pointer-events-none z-0">
            <svg className="w-full h-full" viewBox="0 0 100 40" fill="none" preserveAspectRatio="none">
              <path 
                d="M 0 35 C 45 35, 55 5, 100 5" 
                stroke="#93C5FD" 
                strokeWidth="2" 
                strokeDasharray="4 4" 
                fill="none" 
                className="animate-dash-flow"
              />
            </svg>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 items-start relative z-10">
            {features.map((item, idx) => (
              <React.Fragment key={idx}>
                <motion.div 
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, amount: 0.2 }}
                  transition={{ duration: 0.5, delay: idx * 0.12 }}
                  className={`flex flex-col items-center text-center group cursor-default ${item.offsetClass}`}
                >
                  {/* Clean Icon Circle with #007AFF border & spring hover */}
                  <motion.div 
                    whileHover={{ scale: 1.15, boxShadow: "0 0 20px rgba(0, 122, 255, 0.3)" }}
                    transition={{ type: "spring", stiffness: 350, damping: 20 }}
                    className="w-11 h-11 rounded-full border-2 border-[#007AFF] bg-white flex items-center justify-center mb-4 shadow-xs relative z-10"
                  >
                    {item.icon}
                  </motion.div>
                  
                  <h3 className="text-base sm:text-lg font-bold text-slate-900 mb-2 leading-snug group-hover:text-primary transition-colors">
                    {item.title}
                  </h3>
                  
                  <p className="text-xs sm:text-sm text-slate-500 font-medium leading-relaxed max-w-[240px]">
                    {item.description}
                  </p>
                </motion.div>

                {/* Mobile-only vertical dotted connector between stacked cards */}
                {idx < features.length - 1 && (
                  <div className="md:hidden flex justify-center py-1">
                    <div className="w-0.5 h-6 border-l-2 border-dashed border-blue-300"></div>
                  </div>
                )}
              </React.Fragment>
            ))}
          </div>
        </div>

        {/* CTA Button with Micro-Interaction */}
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.2 }}
          transition={{ duration: 0.5 }}
          className="flex justify-center mb-16"
        >
          <motion.button
            whileHover={{ scale: 1.03, boxShadow: "0 6px 20px rgba(0, 122, 255, 0.3)" }}
            whileTap={{ scale: 0.97 }}
            onClick={() => handleScroll("contact")}
            className="bg-[#007AFF] hover:bg-blue-600 text-white px-8 py-3.5 rounded-xl font-bold transition-all text-sm shadow-md shadow-blue-500/20 active:scale-95"
          >
            Start Your Free Trial
          </motion.button>
        </motion.div>

        {/* Large Hero Image Block with subtle hover depth */}
        <motion.div 
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.2 }}
          transition={{ duration: 0.7 }}
          whileHover={{ scale: 1.008 }}
          className="relative max-w-4xl mx-auto rounded-3xl overflow-hidden shadow-2xl border border-slate-100 transition-transform duration-500"
        >
          <img
            src={CarNavigating}
            alt="Car navigating using Americar"
            className="rounded-3xl w-full max-h-[480px] object-cover"
          />
        </motion.div>


      </div>
    </section>
  );
}




